import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Twitter, Linkedin, MessageCircle, ArrowUpRight } from "lucide-react";
import { CONTACT, SOCIALS, LOGO_URL, COUNTRIES } from "@/data/content";
import { useConsult } from "@/components/ConsultProvider";

const SOCIAL_ICONS = { Instagram, Facebook, "Twitter / X": Twitter, LinkedIn: Linkedin };

const Footer = () => {
  const { openConsult } = useConsult();
  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-navy text-white">
      <div className="noise-overlay absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-20">
        <div className="mb-16 text-center" aria-hidden="true">
          <p className="text-outline-gold font-heading text-[11vw] font-black leading-none tracking-tight md:text-[7vw]">
            STUDENT FIRST
          </p>
        </div>

        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="inline-flex items-center rounded-full bg-white px-5 py-2.5">
              <img src={LOGO_URL} alt="Student First Overseas logo" className="h-11 w-auto" loading="lazy" />
            </span>
            <p className="mt-6 max-w-sm text-lg leading-relaxed text-slate-300">
              Your trusted partner in global education. Uncompromising quality and guidance for every student — from Hyderabad to the world.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.name] || ArrowUpRight;
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`footer-social-${s.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all duration-300 hover:border-gold hover:bg-gold hover:text-navy"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="font-accent text-sm font-bold uppercase tracking-widest text-gold">Explore</h4>
            <ul className="mt-5 space-y-3 text-lg">
              <li><Link to="/" data-testid="footer-link-home" className="text-slate-300 transition-colors hover:text-gold">Home</Link></li>
              <li><Link to="/about" data-testid="footer-link-about" className="text-slate-300 transition-colors hover:text-gold">About Us</Link></li>
              <li><Link to="/admissions" data-testid="footer-link-admissions" className="text-slate-300 transition-colors hover:text-gold">Admissions</Link></li>
              <li><Link to="/visa-guidance" data-testid="footer-link-visa-guidance" className="text-slate-300 transition-colors hover:text-gold">Visa Guidance</Link></li>
              <li><Link to="/blog" data-testid="footer-link-blog" className="text-slate-300 transition-colors hover:text-gold">Blog</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="text-slate-300 transition-colors hover:text-gold">Contact Us</Link></li>
              <li>
                <button onClick={() => openConsult()} data-testid="footer-consult-button" className="text-left text-slate-300 transition-colors hover:text-gold">
                  Free Consultation
                </button>
              </li>
              <li><Link to="/admin" data-testid="footer-link-admin" className="text-sm text-slate-500 transition-colors hover:text-gold">Admin Login</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-accent text-sm font-bold uppercase tracking-widest text-gold">Destinations</h4>
            <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-base">
              {COUNTRIES.map((c) => (
                <li key={c.name}>
                  <button onClick={() => openConsult(c.name)} data-testid={`footer-country-${c.name.toLowerCase().replace(/\s/g, "-")}`} className="text-left text-slate-300 transition-colors hover:text-gold">
                    Study in {c.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-accent text-sm font-bold uppercase tracking-widest text-gold">Reach Us</h4>
            <ul className="mt-5 space-y-4 text-base text-slate-300">
              <li className="flex gap-3">
                <MapPin className="mt-1 h-5 w-5 shrink-0 text-gold" />
                <span>
                  {CONTACT.address}
                  <a
                    href={CONTACT.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-maps-link"
                    className="mt-1 inline-flex items-center gap-1 font-accent text-sm font-semibold text-gold transition-colors hover:text-gold-soft"
                  >
                    Click here for link <ArrowUpRight className="h-4 w-4" />
                  </a>
                </span>
              </li>
              <li className="flex gap-3">
                <Phone className="mt-1 h-5 w-5 shrink-0 text-gold" />
                <span>
                  <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} data-testid="footer-phone-primary" className="block transition-colors hover:text-gold">{CONTACT.phonePrimary}</a>
                  <a href={`tel:${CONTACT.phoneSecondary.replace(/\s/g, "")}`} data-testid="footer-phone-secondary" className="block transition-colors hover:text-gold">{CONTACT.phoneSecondary}</a>
                </span>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-1 h-5 w-5 shrink-0 text-gold" />
                <span>
                  <a href={`mailto:${CONTACT.emailPrimary}`} data-testid="footer-email-primary" className="block break-all transition-colors hover:text-gold">{CONTACT.emailPrimary}</a>
                  <a href={`mailto:${CONTACT.emailSecondary}`} data-testid="footer-email-secondary" className="block break-all transition-colors hover:text-gold">{CONTACT.emailSecondary}</a>
                </span>
              </li>
              <li className="flex gap-3">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-gold" />
                <span>{CONTACT.hours}<br />Sunday: Closed</span>
              </li>
            </ul>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-button"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-accent text-sm font-bold uppercase tracking-widest text-navy transition-transform duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-slate-400 md:flex-row">
          <p>© {new Date().getFullYear()} Student First Overseas Education Consultants Private Limited</p>
          <p>CIN: {CONTACT.cin}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
